import { defineComponent as _defineComponent } from 'vue'
import {
  FloatingActionButton,
} from "@/components/buttons";

import {
  DeleteItemsDialog,
} from "@/components/dialogs";

import {
  computed,
  onMounted,
  ref,
  unref,
  watch,
} from "vue";

import {
  Issuer,
} from "@/model/Issuer";

import {
  useI18n,
} from "@/composables/useI18n";

import {
  httpMethod,
} from "@/scripts/Common/api";

import {
  TopUpRotation,
  WebChargeStatus,
} from "@/Enums";

import {
  WebchargeProtocol,
} from "@/scripts/WebchargeProtocols";

import {
  useWebchargeSettings,
} from "@/composables/useWebchargeSettings";

import {
  useSnackbar,
} from "@/composables/useSnackbar";

import {
  ConfiguredWebcharge,
  ConfiguredWebchargeActions,
  RepeatWebchargeReasons,
  UpdateOrCreateConfiguredWebcharge,
  WebchargeSearchFilter,
} from "@/components/issuers/scripts/configuredWebcharges";

import {
  fetchIssuers,
} from "@/components/issuers/scripts/issuers/requests";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  AddIssuerWebchargesDialog,
  BulkUpdateIssuerWebchargesDialog,
  SearchFilterConfiguredWebcharges,
  WebchargeStateChip,
} from "@/components/issuers/children";

import {
  OperationResults,
} from "@/scripts/Common";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editIssuersPolicy,
} from "@/authorization/policies";

import {
  saveItemToStorage
} from "@/scripts/Common/componentStorage";

import {
  ISSUER_ID_STORAGE_KEY,
} from "@/components/issuers/scripts";

import {
  debounce
} from "@/scripts/Common/HelperFunctions";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS
} from "@/components/issuers/scripts/constants";

import {
  useDataTableDefaults
} from "@/composables";

import {
  createOrUpdateConfiguredWebchargeList,
  deleteConfiguredWebcharges,
  fetchConfiguredWebcharges,
  repeatWebcharge
} from "@/components/issuers/scripts/configuredWebcharges/requests";

import {
  loadIssuerFromStorageOrFirst
} from "./scripts/issuers/helper";

import {
  InfoSnackbar,
} from "@/components/children";

import {
  TooltipListItem,
} from "@/components/base";
import {
  loadCardStates,
  ProfipayCardStates
} from "../cards/scripts";

import {
  isCardActive
} from "@/components/children/cardStatusIcon/scripts/helper";

import
  CardStatusIcon
  from "@/components/children/cardStatusIcon/CardStatusIcon.vue";



export default /*#__PURE__*/_defineComponent({
  __name: 'AdministerIssuerWebcharges',
  emits: [
  'onFetchError',
  'onItemError'
],
  setup(__props, { emit: emits }) {

const {
  t,
  d,
  n
} = useI18n();

const {
  dataTableOptions,
  resetPagination,
  dataTableFooter
} = useDataTableDefaults();



const {
  webchargeSettings,
} = useWebchargeSettings();

const comboSelection = ref<Issuer | undefined>(undefined);
const issuerSearchInput = ref<string | undefined>(undefined);
const selectedConfiguredWebcharges = ref<ConfiguredWebcharge[]>([]);
const availableConfiguredWebcharges = ref<ConfiguredWebcharge[]>([]);
const issuers = ref<Issuer[]>([]);
const selectedIssuer = ref<Issuer>(new Issuer());
const profipayCardStates = ref<ProfipayCardStates[]>([]);
const searchFilterRef = ref();
const snackbarRef = ref(null);
const {
  snackbarStatus,
  snackbarText,
  showSnackbar,
  showSnackbarResponse,
} = useSnackbar(snackbarRef);

const isDeleteDialogVisible = ref<boolean>(false);
const isAddIssuerWebchargesDialogVisible = ref<boolean>(false);
const isUpdateWebchargeDialogVisible = ref<boolean>(false);

let ignoreIssuerSearchInputChange = false; // when the issuer is selected from the combobox, the issuerSearchInput change that follows should not trigger a reload of the issuer list

let queryParams = {
  cardNumber: undefined,
  rotation: undefined,
  nextChargingDate: undefined,
  rotationEnd: undefined
} as WebchargeSearchFilter;

const headers = computed(() => {
  return [
    {
      text: t("webcharge_id"),
      value: "id",
      sortable: false,
    },
    {
      text: t("card_number"),
      value: "card.cardNumber",
    },
    {
      text: t("amount"),
      value: "amount",
    },
    {
      text: t("rotation"),
      value: "rotation"
    },
    {
      text: t("next_charging_date"),
      value: "nextChargingDate"
    },
    {
      text: t("rotation_end"),
      value: "rotationEnd",
    },
    {
      text: t('last_webcharge_state'),
      value: 'lastWebchargeState',
      sortable: false,
    },
    {
      text: t("actions"),
      value: "edit",
      sortable: false,
    }
  ]
});

const getRepeatableInformation = (item: ConfiguredWebcharge) => {
  if (!item.webchargeProtocols) return {isRepeatable: false, reason: 'DEFAULT_REASON'};

  return {
    isRepeatable: item?.webchargeProtocols[0]?.repeatableInformation?.isRepeatable ?? false,
    reason: item?.webchargeProtocols[0]?.repeatableInformation?.reason ?? 'DEFAULT_REASON',
  }
}

const repeatTooltipResolver = computed(() => {
  return {
    [RepeatWebchargeReasons.LastWebchargeSuccessful]: t("repeat_successful_webcharge_error"),
    [RepeatWebchargeReasons.OverflowMonth]: t("repeat_overflow_month_error"),
    [RepeatWebchargeReasons.InstantWebcharge]: t("repeat_instant_webcharge_error"),
    [RepeatWebchargeReasons.Default]: t("repeat_last_webcharge_default_error"),
  }
});

const getLatestWebchargeProtocol = (item: ConfiguredWebcharge) => item.webchargeProtocols?.[0];

const getLastWebchargeState = (item: ConfiguredWebcharge) => getLatestWebchargeProtocol(item)?.state ?? WebChargeStatus.Unknown;

const getLastWebchargeDate = (item: ConfiguredWebcharge) => getLatestWebchargeProtocol(item)?.chargeDate ?? undefined;


const selectedWebchargeIds = computed(
  () => unref(selectedConfiguredWebcharges)?.map((webcharge => webcharge.id))
);

const hasNoEditRights = computed(() => {
  return !hasAccessRights(editIssuersPolicy)
});

const rotations = computed(() => {
  return {
    [TopUpRotation.None]: t("rotation_none"),
    [TopUpRotation.Once]: t("rotation_once"),
    [TopUpRotation.Monthly]: t("rotation_monthly"),
    [TopUpRotation.Quarterly]: t("rotation_quarterly"),
    [TopUpRotation.Yearly]: t("rotation_yearly"),
    [TopUpRotation.Gratification]: t("rotation_gratification"),
  }
});

const localizeCurrency = (value: any) => {
  return n(value, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const localizeDate = (date: any) => {
  const parsedDate = Date.parse(date);
  if (Number.isNaN(parsedDate)) return undefined;

  return d(parsedDate, 'short');
};

const webchargeActionMap = (operationType: string) => {
  const items = {
    [ConfiguredWebchargeActions.CreateConfiguredWebcharges]: t('action_create'),
    [ConfiguredWebchargeActions.UpdateConfiguredWebcharges]: t('action_update'),
    [ConfiguredWebchargeActions.DeleteConfiguredWebcharges]: t('action_delete'),
  };

  return items[operationType as ConfiguredWebchargeActions];
};

const setSnackbarStatus = ({response}: {
  response: Record<any, any>
}) => showSnackbarResponse(response, webchargeActionMap);

const onHandleFetchError = ({error}: { error: any }) => {
  emits('onFetchError', {error})
};

const onHandleItemError = ({response}: { response: OperationResults<any> }) => {
  emits('onItemError', {response})
};

const initializeIssuer = async () => {
  const {response, error} = await loadIssuerFromStorageOrFirst();
  if (error) {
    onHandleFetchError({error});
    return;
  }

  selectedIssuer.value = response ?? new Issuer();
  comboSelection.value = response;
}

const loadProfipayCardStates = async (cardNumbers: number[]) => {

  if (cardNumbers.length === 0) return;

  // we will send a request with the card numbers as a filter
  // therefore we want to return the first page with the same amount of items as the card numbers that we send
  const {
    error,
    response,
  } = await loadCardStates(cardNumbers, {page: 1, itemsPerPage: cardNumbers.length});

  if (error) {
    onHandleFetchError({error});
    return;
  }

  profipayCardStates.value = response;
}

const loadIssuers = async () => {

  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if (issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
}

const loadConfiguredWebcharges = async () => {
  if (selectedIssuer.value.IsNew) return;

  const {
    error,
    response,
  } = await fetchConfiguredWebcharges(selectedIssuer.value.Id, dataTableOptions, queryParams);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  // get distinct card numbers
  const cardNumbers = (response as ConfiguredWebcharge[])
    .map(item => item.card.cardNumber)
    .filter((value, index, self) => self.indexOf(value) === index);

  // fetch the card states before setting availableConfiguredWebcharges.value to avoid flickering in the ui
  // where the table contents change (state) after the table is rendered
  // or in other terms: only render page once all displayed data is complete
  await loadProfipayCardStates(cardNumbers);

  availableConfiguredWebcharges.value = response;
}

const onHandleButtonRefresh = async () => {
  await loadIssuers();
  await loadConfiguredWebcharges();
};

const removeConfiguredWebcharges = async (listOfWebchargeIds: number[]) => {
  const {error, response} = await deleteConfiguredWebcharges(listOfWebchargeIds);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  setSnackbarStatus({response});
  await loadConfiguredWebcharges();
  resetSelectedWebcharges();
}

const createOrUpdateConfiguredWebcharges = async (method: httpMethod, webcharges: UpdateOrCreateConfiguredWebcharge[]) => {
  const {error, response} = await createOrUpdateConfiguredWebchargeList(method, webcharges);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  setSnackbarStatus({response});

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  await loadConfiguredWebcharges();
}

const repeatWebchargeMessage = computed(() => {
  return {
    [WebChargeStatus.All]: t('state_unknown'),
    [WebChargeStatus.Success]: t('state_success'),
    [WebChargeStatus.Unknown]: t('state_unknown'),
    [WebChargeStatus.MaxChargeLimitError]: t('state_max_charge_limit_error'),
    [WebChargeStatus.ConfigurationError]: t('state_configuration_error'),
    [WebChargeStatus.MaxCardLimitError]: t('state_max_card_limit_error'),
    [WebChargeStatus.SystemError]: t('state_system_error'),
    [WebChargeStatus.MinCardLimitError]: t('state_min_card_limit_error'),
    [WebChargeStatus.WebchargeDisabled]: t('state_webcharge_disabled'),
  }
});

const repeatLastWebcharge = async (lastWebcharge: WebchargeProtocol) => {
  const {response, error} = await repeatWebcharge(lastWebcharge.id);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  const statusText = [repeatWebchargeMessage.value[response.webchargeState as WebChargeStatus]] ?? t('state_unknown');
  const status = response.webchargeState !== WebChargeStatus.Success ? 'warning' : 'success'

  showSnackbar({status, statusText})

  await loadConfiguredWebcharges();
}

const onHandleSearchFilter = (searchFilter: WebchargeSearchFilter) => {
  queryParams = {...searchFilter};
  dataTableOptions.value.pagination.page = 1;
  resetSelectedWebcharges();
  loadConfiguredWebcharges();
}

const onHandleButtonDeleteBatch = () => {
  isDeleteDialogVisible.value = true;
};

const onHandleItemDeleteClick = (item: ConfiguredWebcharge) => {
  selectedConfiguredWebcharges.value = [item];
  isDeleteDialogVisible.value = true;
};

const onHandleDialogDeleteConfirm = () => {
  isDeleteDialogVisible.value = false;
  removeConfiguredWebcharges(unref(selectedWebchargeIds));
};

const onHandleDialogDeleteCancel = () => {
  isDeleteDialogVisible.value = false;
};

const onHandleItemRepeatWebchargeClick = (item: ConfiguredWebcharge) => {
  if (!item.webchargeProtocols || item.webchargeProtocols.length === 0) return;

  const lastWebcharge = item.webchargeProtocols[0];

  repeatLastWebcharge(lastWebcharge);
}

const onHandleDialogUpdateConfirm = async (webchargesToUpdate: UpdateOrCreateConfiguredWebcharge[]) => {
  await createOrUpdateConfiguredWebcharges(httpMethod.PUT, unref(webchargesToUpdate));
  await loadConfiguredWebcharges();
  isUpdateWebchargeDialogVisible.value = false;
};

const onHandleDialogUpdateCancel = () => {
  isUpdateWebchargeDialogVisible.value = false;
};
const onHandleButtonUpdateBatch = () => {
  isUpdateWebchargeDialogVisible.value = true;
};

const onHandleAddConfiguredWebcharge = () => {
  isAddIssuerWebchargesDialogVisible.value = true;
};

const onHandleDialogAddConfirm = async (webchargesToCreate: UpdateOrCreateConfiguredWebcharge[]) => {
  await createOrUpdateConfiguredWebcharges(httpMethod.POST, unref(webchargesToCreate));
  isAddIssuerWebchargesDialogVisible.value = false
}

const onHandleDialogAddCancel = () => {
  isAddIssuerWebchargesDialogVisible.value = false;
}

const onHandleItemUpdateClick = (item: ConfiguredWebcharge) => {
  selectedConfiguredWebcharges.value = [item];
  isUpdateWebchargeDialogVisible.value = true;
};

const resetSelectedWebcharges = () => {
  selectedConfiguredWebcharges.value = [];
}

watch(() => comboSelection.value, async () => {
  if (comboSelection.value && comboSelection.value instanceof Issuer) {
    selectedIssuer.value = comboSelection.value;
    await loadConfiguredWebcharges();
  }
});

watch(() => [
  dataTableOptions.value.pagination.page,
  dataTableOptions.value.pagination.itemsPerPage,
  dataTableOptions.value.sorting,
], async () => {
  resetSelectedWebcharges();
  await loadConfiguredWebcharges();
}, {deep: true});

const onHandleSelectionChange = () => {
  const issuerId = comboSelection.value?.Id;
  if (!issuerId || !(comboSelection.value instanceof Issuer)) return;
  saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY);

  resetSelectedWebcharges();
  resetPagination();
  searchFilterRef.value.resetFilters();
}

watch(() => issuerSearchInput.value, debounce(async () => {
  if (ignoreIssuerSearchInputChange) {
    ignoreIssuerSearchInputChange = false;
    return;
  }
  await loadIssuers();
}));

onMounted(async () => {
  await initializeIssuer();
  await loadConfiguredWebcharges();
});


return { __sfc: true,t, d, n, dataTableOptions, resetPagination, dataTableFooter, emits, webchargeSettings, comboSelection, issuerSearchInput, selectedConfiguredWebcharges, availableConfiguredWebcharges, issuers, selectedIssuer, profipayCardStates, searchFilterRef, snackbarRef, snackbarStatus, snackbarText, showSnackbar, showSnackbarResponse, isDeleteDialogVisible, isAddIssuerWebchargesDialogVisible, isUpdateWebchargeDialogVisible, ignoreIssuerSearchInputChange, queryParams, headers, getRepeatableInformation, repeatTooltipResolver, getLatestWebchargeProtocol, getLastWebchargeState, getLastWebchargeDate, selectedWebchargeIds, hasNoEditRights, rotations, localizeCurrency, localizeDate, webchargeActionMap, setSnackbarStatus, onHandleFetchError, onHandleItemError, initializeIssuer, loadProfipayCardStates, loadIssuers, loadConfiguredWebcharges, onHandleButtonRefresh, removeConfiguredWebcharges, createOrUpdateConfiguredWebcharges, repeatWebchargeMessage, repeatLastWebcharge, onHandleSearchFilter, onHandleButtonDeleteBatch, onHandleItemDeleteClick, onHandleDialogDeleteConfirm, onHandleDialogDeleteCancel, onHandleItemRepeatWebchargeClick, onHandleDialogUpdateConfirm, onHandleDialogUpdateCancel, onHandleButtonUpdateBatch, onHandleAddConfiguredWebcharge, onHandleDialogAddConfirm, onHandleDialogAddCancel, onHandleItemUpdateClick, resetSelectedWebcharges, onHandleSelectionChange, FloatingActionButton, DeleteItemsDialog, AddIssuerWebchargesDialog, BulkUpdateIssuerWebchargesDialog, SearchFilterConfiguredWebcharges, WebchargeStateChip, InfoSnackbar, TooltipListItem, isCardActive, CardStatusIcon }
}

})